<template>
  <div class="cms-dashboard">
    <cms-page-header
      :title="$t('cms.dashboard.title')"
      :subtitle="$t('cms.dashboard.subtitle')"
      :show-create="false"
    />

    <div v-if="isLoading" class="cms-dashboard-loading">
      <div class="cms-loading-spinner"></div>
      <p>{{ $t('general.shared.loading') }}</p>
    </div>

    <template v-else>
      <!-- Welcome band -->
      <section class="cms-dash-hero" :class="{ 'is-maintenance': maintenanceEnabled }">
        <div class="cms-dash-hero-copy">
          <p class="cms-dash-hero-eyebrow">{{ greeting }}</p>
          <h2 class="cms-dash-hero-title">{{ agencyName }}</h2>
          <p class="cms-dash-hero-text">
            {{ $t('cms.dashboard.heroSummary', { count: totalContent }) }}
          </p>
        </div>
        <div class="cms-dash-hero-pulse">
          <div class="cms-dash-ring" :style="publishRingStyle">
            <div class="cms-dash-ring-inner">
              <span class="cms-dash-ring-value">{{ publishRate }}%</span>
              <span class="cms-dash-ring-label">{{ $t('cms.dashboard.publishRate') }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Attention strip -->
      <div v-if="attentionItems.length" class="cms-dash-attention">
        <button
          v-for="item in attentionItems"
          :key="item.id"
          type="button"
          class="cms-dash-attention-card"
          :class="`tone-${item.tone}`"
          @click="goTo(item.routeName, item.query)"
        >
          <span class="cms-dash-attention-dot" />
          <div>
            <p class="cms-dash-attention-title">{{ item.title }}</p>
            <p class="cms-dash-attention-meta">{{ item.meta }}</p>
          </div>
          <v-icon icon="arrow-right" size="sm" class="cms-dash-attention-arrow" />
        </button>
      </div>

      <!-- Colored KPI cards -->
      <div class="cms-dash-stats">
        <button
          v-for="card in statCards"
          :key="card.routeName"
          type="button"
          class="cms-dash-stat"
          :style="{ '--accent': card.color, '--accent-soft': card.soft }"
          @click="goTo(card.routeName)"
        >
          <div class="cms-dash-stat-head">
            <span class="cms-dash-stat-icon">
              <v-icon :icon="card.icon" size="sm" />
            </span>
            <span>{{ card.label }}</span>
          </div>
          <p class="cms-dash-stat-value">{{ animated[card.key] ?? card.value }}</p>
          <div class="cms-dash-stat-bar" aria-hidden="true">
            <span :style="{ width: `${card.ratio}%` }" />
          </div>
          <p class="cms-dash-stat-meta">{{ card.meta }}</p>
        </button>
      </div>

      <div class="cms-dash-panels">
        <!-- Content share: stacked bar + ranked list -->
        <section class="cms-dash-panel">
          <div class="cms-dash-panel-head">
            <h3>{{ $t('cms.dashboard.contentMix') }}</h3>
            <p>{{ $t('cms.dashboard.contentMixHint') }}</p>
          </div>

          <div
            class="cms-dash-stack"
            role="img"
            :aria-label="$t('cms.dashboard.contentMix')"
          >
            <button
              v-for="seg in contentBreakdown"
              :key="`stack-${seg.key}`"
              type="button"
              class="cms-dash-stack-seg"
              :style="{ flexGrow: Math.max(seg.pct, seg.value > 0 ? 2 : 0), background: seg.color }"
              :title="`${seg.label}: ${seg.value} (${seg.pct}%)`"
              :disabled="!seg.value"
              @click="goTo(seg.routeName)"
            />
          </div>
          <p class="cms-dash-stack-total">
            {{ totalContent }} {{ $t('cms.dashboard.items') }}
          </p>

          <div class="cms-dash-breakdown">
            <button
              v-for="row in contentBreakdown"
              :key="row.key"
              type="button"
              class="cms-dash-breakdown-row"
              @click="goTo(row.routeName)"
            >
              <span class="cms-dash-breakdown-swatch" :style="{ background: row.color }" />
              <span class="cms-dash-breakdown-label">{{ row.label }}</span>
              <span class="cms-dash-breakdown-count">{{ row.value }}</span>
              <span class="cms-dash-breakdown-pct">{{ row.pct }}%</span>
              <span class="cms-dash-breakdown-meter" aria-hidden="true">
                <span :style="{ width: `${row.pct}%`, background: row.color }" />
              </span>
            </button>
          </div>
        </section>

        <!-- Live coverage per section -->
        <section class="cms-dash-panel">
          <div class="cms-dash-panel-head">
            <h3>{{ $t('cms.dashboard.healthTitle') }}</h3>
            <p>{{ $t('cms.dashboard.healthHint') }}</p>
          </div>
          <div class="cms-dash-coverage">
            <button
              v-for="row in publishCoverage"
              :key="row.key"
              type="button"
              class="cms-dash-coverage-row"
              @click="goTo(row.routeName)"
            >
              <div class="cms-dash-coverage-top">
                <span class="cms-dash-coverage-label">
                  <span class="cms-dash-breakdown-swatch" :style="{ background: row.color }" />
                  {{ row.label }}
                </span>
                <span class="cms-dash-coverage-meta">
                  {{
                    $t('cms.dashboard.liveOfTotal', {
                      live: row.live,
                      total: row.total,
                    })
                  }}
                </span>
              </div>
              <div class="cms-dash-coverage-track" aria-hidden="true">
                <span
                  class="cms-dash-coverage-live"
                  :style="{ width: `${row.livePct}%`, background: row.color }"
                />
              </div>
            </button>
          </div>
        </section>
      </div>

      <!-- Quick goto -->
      <section class="cms-dash-quick">
        <div class="cms-dash-panel-head">
          <h3>{{ $t('cms.dashboard.quickGoto') }}</h3>
          <p>{{ $t('cms.dashboard.quickGotoHint') }}</p>
        </div>
        <div class="cms-dash-quick-grid">
          <button
            v-for="item in quickLinks"
            :key="item.routeName + (item.query?.tab || '')"
            type="button"
            class="cms-dash-quick-item"
            :style="{ '--accent': item.color }"
            @click="goTo(item.routeName, item.query)"
          >
            <span class="cms-dash-quick-icon">
              <v-icon :icon="item.icon" size="sm" />
            </span>
            <span>{{ item.label }}</span>
          </button>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import api from '@/api';
import { CmsPageHeader } from '@/components/cms';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';

interface DashboardStats {
  activities: { total: number; public: number };
  stories: { total: number; public: number };
  pages: { total: number; public: number };
  reviews: { total: number; pending: number; approved: number };
  contact_messages: { total: number; new: number };
  guiders: { total: number; public: number };
  team_members: { total: number; public: number };
  maintenance: { enabled: boolean };
}

const emptyStats = (): DashboardStats => ({
  activities: { total: 0, public: 0 },
  stories: { total: 0, public: 0 },
  pages: { total: 0, public: 0 },
  reviews: { total: 0, pending: 0, approved: 0 },
  contact_messages: { total: 0, new: 0 },
  guiders: { total: 0, public: 0 },
  team_members: { total: 0, public: 0 },
  maintenance: { enabled: false },
});

const COLORS = {
  activities: { solid: '#e61275', soft: 'rgba(230, 18, 117, 0.12)' },
  stories: { solid: '#e65c51', soft: 'rgba(230, 92, 81, 0.12)' },
  pages: { solid: '#b80a5e', soft: 'rgba(184, 10, 94, 0.12)' },
  reviews: { solid: '#ff3a92', soft: 'rgba(255, 58, 146, 0.14)' },
  contact: { solid: '#992a24', soft: 'rgba(153, 42, 36, 0.12)' },
  guiders: { solid: '#d50d6e', soft: 'rgba(213, 12, 110, 0.12)' },
  team: { solid: '#ff7769', soft: 'rgba(255, 119, 105, 0.14)' },
  media: { solid: '#2c2b2b', soft: 'rgba(44, 43, 43, 0.08)' },
  settings: { solid: '#3c3c3c', soft: 'rgba(60, 60, 60, 0.1)' },
};

export default defineComponent({
  name: 'TheCmsDashboard',
  components: {
    CmsPageHeader,
    VIcon,
  },
  setup() {
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();
    const isLoading = ref(true);
    const stats = ref<DashboardStats>(emptyStats());
    const animated = ref<Record<string, number>>({});

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const agencyName = computed(
      () => authStore.agency?.name || authStore.agency?.slug || 'WAW Travel'
    );
    const maintenanceEnabled = computed(() => Boolean(stats.value.maintenance?.enabled));

    const greeting = computed(() => {
      const hour = new Date().getHours();
      if (hour < 12) return t('cms.dashboard.greetingMorning');
      if (hour < 18) return t('cms.dashboard.greetingAfternoon');
      return t('cms.dashboard.greetingEvening');
    });

    const normalizeStats = (data: any): DashboardStats => {
      const raw = data || {};
      return {
        activities: {
          total: Number(raw.activities?.total ?? 0),
          public: Number(raw.activities?.public ?? 0),
        },
        stories: {
          total: Number(raw.stories?.total ?? 0),
          public: Number(raw.stories?.public ?? 0),
        },
        pages: {
          total: Number(raw.pages?.total ?? 0),
          public: Number(raw.pages?.public ?? 0),
        },
        reviews: {
          total: Number(raw.reviews?.total ?? 0),
          pending: Number(raw.reviews?.pending ?? 0),
          approved: Number(raw.reviews?.approved ?? 0),
        },
        contact_messages: {
          total: Number(raw.contactMessages?.total ?? raw.contact_messages?.total ?? 0),
          new: Number(raw.contactMessages?.new ?? raw.contact_messages?.new ?? 0),
        },
        guiders: {
          total: Number(raw.guiders?.total ?? 0),
          public: Number(raw.guiders?.public ?? 0),
        },
        team_members: {
          total: Number(raw.teamMembers?.total ?? raw.team_members?.total ?? 0),
          public: Number(raw.teamMembers?.public ?? raw.team_members?.public ?? 0),
        },
        maintenance: {
          enabled: Boolean(raw.maintenance?.enabled ?? false),
        },
      };
    };

    const ratio = (publicCount: number, total: number) => {
      if (!total) return 0;
      return Math.max(6, Math.round((publicCount / total) * 100));
    };

    const totalContent = computed(() => {
      const s = stats.value;
      return (
        s.activities.total +
        s.stories.total +
        s.pages.total +
        s.reviews.total +
        s.guiders.total +
        s.team_members.total
      );
    });

    const publicContent = computed(() => {
      const s = stats.value;
      return (
        s.activities.public +
        s.stories.public +
        s.pages.public +
        s.reviews.approved +
        s.guiders.public +
        s.team_members.public
      );
    });

    const publishRate = computed(() => {
      if (!totalContent.value) return 0;
      return Math.round((publicContent.value / totalContent.value) * 100);
    });

    const publishRingStyle = computed(() => {
      const pct = publishRate.value;
      return {
        background: `conic-gradient(#e61275 ${pct}%, rgba(230, 18, 117, 0.16) 0)`,
      };
    });

    const attentionItems = computed(() => {
      const items: Array<{
        id: string;
        title: string;
        meta: string;
        tone: string;
        routeName: string;
        query?: Record<string, string>;
      }> = [];
      const s = stats.value;

      if (s.maintenance.enabled) {
        items.push({
          id: 'maintenance',
          title: t('cms.dashboard.attention.maintenanceTitle'),
          meta: t('cms.dashboard.attention.maintenanceMeta'),
          tone: 'warn',
          routeName: 'cms.settings',
          query: { tab: 'maintenance' },
        });
      }
      if (s.reviews.pending > 0) {
        items.push({
          id: 'reviews',
          title: t('cms.dashboard.attention.reviewsTitle', { count: s.reviews.pending }),
          meta: t('cms.dashboard.attention.reviewsMeta'),
          tone: 'purple',
          routeName: 'cms.reviews',
        });
      }
      if (s.contact_messages.new > 0) {
        items.push({
          id: 'inbox',
          title: t('cms.dashboard.attention.inboxTitle', { count: s.contact_messages.new }),
          meta: t('cms.dashboard.attention.inboxMeta'),
          tone: 'rose',
          routeName: 'cms.contact-messages',
        });
      }
      return items;
    });

    const statCards = computed(() => {
      const s = stats.value;
      return [
        {
          key: 'activities',
          label: t('cms.dashboard.stats.activities'),
          value: s.activities.total,
          meta: `${s.activities.public} ${t('cms.dashboard.stats.public')}`,
          icon: 'map',
          routeName: 'cms.activities',
          color: COLORS.activities.solid,
          soft: COLORS.activities.soft,
          ratio: ratio(s.activities.public, s.activities.total),
        },
        {
          key: 'stories',
          label: t('cms.dashboard.stats.stories'),
          value: s.stories.total,
          meta: `${s.stories.public} ${t('cms.dashboard.stats.public')}`,
          icon: 'quote',
          routeName: 'cms.stories',
          color: COLORS.stories.solid,
          soft: COLORS.stories.soft,
          ratio: ratio(s.stories.public, s.stories.total),
        },
        {
          key: 'pages',
          label: t('cms.dashboard.stats.pages'),
          value: s.pages.total,
          meta: `${s.pages.public} ${t('cms.dashboard.stats.public')}`,
          icon: 'description',
          routeName: 'cms.pages',
          color: COLORS.pages.solid,
          soft: COLORS.pages.soft,
          ratio: ratio(s.pages.public, s.pages.total),
        },
        {
          key: 'reviews',
          label: t('cms.dashboard.stats.reviews'),
          value: s.reviews.total,
          meta: `${s.reviews.pending} ${t('cms.dashboard.stats.pending')}`,
          icon: 'happy',
          routeName: 'cms.reviews',
          color: COLORS.reviews.solid,
          soft: COLORS.reviews.soft,
          ratio: ratio(s.reviews.approved, s.reviews.total || 1),
        },
        {
          key: 'contact',
          label: t('cms.dashboard.stats.contactMessages'),
          value: s.contact_messages.total,
          meta: `${s.contact_messages.new} ${t('cms.dashboard.stats.new')}`,
          icon: 'mail',
          routeName: 'cms.contact-messages',
          color: COLORS.contact.solid,
          soft: COLORS.contact.soft,
          ratio: s.contact_messages.total
            ? ratio(s.contact_messages.total - s.contact_messages.new, s.contact_messages.total)
            : 0,
        },
        {
          key: 'guiders',
          label: t('cms.dashboard.stats.guiders'),
          value: s.guiders.total,
          meta: `${s.guiders.public} ${t('cms.dashboard.stats.public')}`,
          icon: 'compass',
          routeName: 'cms.guiders',
          color: COLORS.guiders.solid,
          soft: COLORS.guiders.soft,
          ratio: ratio(s.guiders.public, s.guiders.total),
        },
        {
          key: 'team',
          label: t('cms.dashboard.stats.teamMembers'),
          value: s.team_members.total,
          meta: `${s.team_members.public} ${t('cms.dashboard.stats.public')}`,
          icon: 'user',
          routeName: 'cms.team-members',
          color: COLORS.team.solid,
          soft: COLORS.team.soft,
          ratio: ratio(s.team_members.public, s.team_members.total),
        },
      ];
    });

    const contentBreakdown = computed(() => {
      const rows = [
        {
          key: 'activities',
          label: t('cms.dashboard.stats.activities'),
          value: stats.value.activities.total,
          color: COLORS.activities.solid,
          routeName: 'cms.activities',
        },
        {
          key: 'stories',
          label: t('cms.dashboard.stats.stories'),
          value: stats.value.stories.total,
          color: COLORS.stories.solid,
          routeName: 'cms.stories',
        },
        {
          key: 'pages',
          label: t('cms.dashboard.stats.pages'),
          value: stats.value.pages.total,
          color: COLORS.pages.solid,
          routeName: 'cms.pages',
        },
        {
          key: 'reviews',
          label: t('cms.dashboard.stats.reviews'),
          value: stats.value.reviews.total,
          color: COLORS.reviews.solid,
          routeName: 'cms.reviews',
        },
        {
          key: 'guiders',
          label: t('cms.dashboard.stats.guiders'),
          value: stats.value.guiders.total,
          color: COLORS.guiders.solid,
          routeName: 'cms.guiders',
        },
        {
          key: 'team',
          label: t('cms.dashboard.stats.teamMembers'),
          value: stats.value.team_members.total,
          color: COLORS.team.solid,
          routeName: 'cms.team-members',
        },
      ];
      const total = rows.reduce((sum, row) => sum + row.value, 0) || 1;
      return rows
        .map((row) => ({
          ...row,
          pct: Math.round((row.value / total) * 100),
        }))
        .sort((a, b) => b.value - a.value);
    });

    const publishCoverage = computed(() => {
      const rows = [
        {
          key: 'activities',
          label: t('cms.dashboard.stats.activities'),
          live: stats.value.activities.public,
          total: stats.value.activities.total,
          color: COLORS.activities.solid,
          routeName: 'cms.activities',
        },
        {
          key: 'stories',
          label: t('cms.dashboard.stats.stories'),
          live: stats.value.stories.public,
          total: stats.value.stories.total,
          color: COLORS.stories.solid,
          routeName: 'cms.stories',
        },
        {
          key: 'pages',
          label: t('cms.dashboard.stats.pages'),
          live: stats.value.pages.public,
          total: stats.value.pages.total,
          color: COLORS.pages.solid,
          routeName: 'cms.pages',
        },
        {
          key: 'reviews',
          label: t('cms.dashboard.stats.reviews'),
          live: stats.value.reviews.approved,
          total: stats.value.reviews.total,
          color: COLORS.reviews.solid,
          routeName: 'cms.reviews',
        },
        {
          key: 'guiders',
          label: t('cms.dashboard.stats.guiders'),
          live: stats.value.guiders.public,
          total: stats.value.guiders.total,
          color: COLORS.guiders.solid,
          routeName: 'cms.guiders',
        },
        {
          key: 'team',
          label: t('cms.dashboard.stats.teamMembers'),
          live: stats.value.team_members.public,
          total: stats.value.team_members.total,
          color: COLORS.team.solid,
          routeName: 'cms.team-members',
        },
      ];

      return rows.map((row) => ({
        ...row,
        livePct: row.total ? Math.round((row.live / row.total) * 100) : 0,
      }));
    });

    const quickLinks = computed(() => [
      { label: t('cms.menu.activities'), icon: 'map', routeName: 'cms.activities', color: COLORS.activities.solid },
      { label: t('cms.menu.stories'), icon: 'quote', routeName: 'cms.stories', color: COLORS.stories.solid },
      { label: t('cms.menu.pages'), icon: 'description', routeName: 'cms.pages', color: COLORS.pages.solid },
      { label: t('cms.menu.media'), icon: 'picture', routeName: 'cms.media', color: COLORS.media.solid },
      { label: t('cms.menu.reviews'), icon: 'happy', routeName: 'cms.reviews', color: COLORS.reviews.solid },
      { label: t('cms.menu.contactMessages'), icon: 'mail', routeName: 'cms.contact-messages', color: COLORS.contact.solid },
      { label: t('cms.menu.faq'), icon: 'question', routeName: 'cms.help-center', color: COLORS.guiders.solid },
      { label: t('cms.menu.settings'), icon: 'settings', routeName: 'cms.settings', color: COLORS.settings.solid },
    ]);

    const animateCounts = () => {
      const targets = Object.fromEntries(statCards.value.map((c) => [c.key, c.value]));
      const duration = 700;
      const start = performance.now();
      const from = { ...animated.value };

      const tick = (now: number) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        const next: Record<string, number> = {};
        Object.keys(targets).forEach((key) => {
          const begin = from[key] || 0;
          next[key] = Math.round(begin + (targets[key] - begin) * eased);
        });
        animated.value = next;
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    watch(
      () => statCards.value.map((c) => c.value).join(','),
      () => animateCounts()
    );

    const goTo = (routeName: string, query?: Record<string, string>) => {
      router.push({ name: routeName, query });
    };

    const loadStats = async () => {
      if (!agencySlug.value) {
        isLoading.value = false;
        return;
      }

      isLoading.value = true;
      try {
        const response = await api.dashboardService.getCmsDashboardStats(agencySlug.value);
        const data = response?.data || response || {};
        stats.value = normalizeStats(data);
      } catch (error: any) {
        console.error('Error loading dashboard stats', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      loadStats();
    });

    return {
      isLoading,
      maintenanceEnabled,
      greeting,
      agencyName,
      totalContent,
      publishRate,
      publishRingStyle,
      attentionItems,
      statCards,
      contentBreakdown,
      publishCoverage,
      quickLinks,
      animated,
      goTo,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-dashboard {
  color: $cms-text-primary;
}

.cms-dashboard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: $cms-text-secondary;
}

.cms-dash-hero {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1.25rem;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1.35rem 1.4rem;
  border-radius: 18px;
  border: 1px solid rgba(230, 18, 117, 0.18);
  background:
    radial-gradient(circle at top right, rgba(255, 58, 146, 0.35), transparent 42%),
    linear-gradient(135deg, #2c2b2b 0%, #3c3c3c 42%, #b80a5e 78%, #e61275 100%);
  color: #fff;
  overflow: hidden;
  position: relative;

  &.is-maintenance {
    background:
      radial-gradient(circle at top right, rgba(230, 92, 81, 0.35), transparent 42%),
      linear-gradient(135deg, #2c2b2b 0%, #992a24 55%, #e65c51 100%);
  }

  @media (max-width: $cms-breakpoint-mobile) {
    grid-template-columns: 1fr;
  }
}

.cms-dash-hero-eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.72;
}

.cms-dash-hero-title {
  margin: 0 0 0.45rem;
  font-size: clamp(1.4rem, 2.4vw, 1.9rem);
  font-weight: 700;
  line-height: 1.15;
}

.cms-dash-hero-text {
  margin: 0;
  max-width: 38rem;
  color: rgba(248, 250, 252, 0.78);
  font-size: 0.95rem;
  line-height: 1.5;
}

.cms-dash-ring {
  width: 108px;
  height: 108px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}

.cms-dash-ring-inner {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background: rgba(44, 43, 43, 0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.cms-dash-ring-value {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1;
}

.cms-dash-ring-label {
  margin-top: 0.2rem;
  font-size: 0.65rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.7;
}

.cms-dash-attention {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.cms-dash-attention-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.75rem;
  align-items: center;
  text-align: left;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  }

  &.tone-warn {
    background: #fadcd7;
    border-color: rgba(230, 92, 81, 0.28);
    .cms-dash-attention-dot { background: #e65c51; }
  }

  &.tone-purple {
    background: #ffd0e6;
    border-color: rgba(230, 18, 117, 0.22);
    .cms-dash-attention-dot { background: #e61275; }
  }

  &.tone-rose {
    background: #ffd0e6;
    border-color: rgba(184, 10, 94, 0.22);
    .cms-dash-attention-dot { background: #b80a5e; }
  }
}

.cms-dash-attention-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.cms-dash-attention-title {
  margin: 0;
  font-weight: 700;
  font-size: 0.92rem;
}

.cms-dash-attention-meta {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: $cms-text-secondary;
}

.cms-dash-attention-arrow {
  color: $cms-text-muted;
}

.cms-dash-stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: 0.85rem;
  margin-bottom: 1.15rem;
}

.cms-dash-stat {
  text-align: left;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  background:
    linear-gradient(180deg, #fff 0%, #fff 60%, var(--accent-soft) 160%);
  padding: 1rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: color-mix(in srgb, var(--accent) 35%, white);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
  }
}

.cms-dash-stat-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: $cms-text-secondary;
  font-size: 0.82rem;
  font-weight: 650;
}

.cms-dash-stat-icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: var(--accent-soft);
  color: var(--accent);
}

.cms-dash-stat-value {
  margin: 0.7rem 0 0.45rem;
  font-size: 1.85rem;
  font-weight: 750;
  line-height: 1;
  letter-spacing: -0.03em;
  color: $cms-text-primary;
  font-variant-numeric: tabular-nums;
}

.cms-dash-stat-bar {
  height: 5px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  overflow: hidden;
  margin-bottom: 0.45rem;

  span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--accent);
    transition: width 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  }
}

.cms-dash-stat-meta {
  margin: 0;
  font-size: 0.78rem;
  color: $cms-text-muted;
}

.cms-dash-panels {
  display: grid;
  grid-template-columns: 1.2fr 0.9fr;
  gap: 0.9rem;
  margin-bottom: 1.15rem;

  @media (max-width: $cms-breakpoint-tablet) {
    grid-template-columns: 1fr;
  }
}

.cms-dash-panel,
.cms-dash-quick {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  background: #fff;
  padding: 1.1rem 1.15rem;
}

.cms-dash-panel-head {
  margin-bottom: 1rem;

  h3 {
    margin: 0 0 0.25rem;
    font-size: 1rem;
  }

  p {
    margin: 0;
    color: $cms-text-secondary;
    font-size: 0.82rem;
  }
}

.cms-dash-stack {
  display: flex;
  width: 100%;
  height: 14px;
  border-radius: 999px;
  overflow: hidden;
  background: #f1f5f9;
  gap: 2px;
}

.cms-dash-stack-seg {
  min-width: 0;
  height: 100%;
  border: 0;
  padding: 0;
  cursor: pointer;
  transition: filter 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }

  &:disabled {
    cursor: default;
    flex-grow: 0 !important;
    width: 0;
  }
}

.cms-dash-stack-total {
  margin: 0.55rem 0 1rem;
  font-size: 0.78rem;
  color: $cms-text-muted;
  font-weight: 600;
}

.cms-dash-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.cms-dash-breakdown-row {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto auto;
  grid-template-areas:
    'swatch label count pct'
    '. meter meter meter';
  gap: 0.2rem 0.55rem;
  align-items: center;
  width: 100%;
  border: 0;
  border-radius: 10px;
  background: transparent;
  padding: 0.45rem 0.4rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(230, 18, 117, 0.05);
  }
}

.cms-dash-breakdown-swatch {
  grid-area: swatch;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.cms-dash-breakdown-label {
  grid-area: label;
  font-size: 0.84rem;
  font-weight: 600;
  color: $cms-text-primary;
}

.cms-dash-breakdown-count {
  grid-area: count;
  font-size: 0.9rem;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
  color: $cms-text-primary;
}

.cms-dash-breakdown-pct {
  grid-area: pct;
  min-width: 2.6rem;
  text-align: right;
  font-size: 0.78rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: $cms-text-muted;
}

.cms-dash-breakdown-meter {
  grid-area: meter;
  height: 5px;
  border-radius: 999px;
  background: #f1f5f9;
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    border-radius: inherit;
    transition: width 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  }
}

.cms-dash-coverage {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.cms-dash-coverage-row {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;

  &:hover .cms-dash-coverage-label {
    color: $cms-primary-color;
  }
}

.cms-dash-coverage-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.cms-dash-coverage-label {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.84rem;
  font-weight: 600;
  color: $cms-text-primary;
  transition: color 0.15s ease;
}

.cms-dash-coverage-meta {
  font-size: 0.78rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: $cms-text-muted;
  white-space: nowrap;
}

.cms-dash-coverage-track {
  height: 8px;
  border-radius: 999px;
  background: #f1f5f9;
  overflow: hidden;
}

.cms-dash-coverage-live {
  display: block;
  height: 100%;
  border-radius: inherit;
  min-width: 0;
  transition: width 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.cms-dash-quick-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 0.65rem;
}

.cms-dash-quick-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 0.9rem;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  cursor: pointer;
  font-weight: 650;
  color: $cms-text-primary;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: color-mix(in srgb, var(--accent) 35%, white);
    background: color-mix(in srgb, var(--accent) 8%, white);
  }
}

.cms-dash-quick-icon {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--accent) 14%, white);
  color: var(--accent);
}
</style>
